import { FunctionalComponent, h } from 'preact';
import { useContext, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { sendPostMessage } from 'helpers/post-message.helper';
import { loadDeferredJs } from 'helpers/scripts-loader';
import { ApplicationContext } from 'services/use-application-setup';
import { Language, LANGUAGES } from 'constants/languages';
import './language-selector.scss';
import { CircleFlag, CustomScroll } from '@uw/common-components';
import { useConnect } from '../../helpers/use-connect-to-global-store';
import { sessionStore } from '../../global/session.store';
import { useLanguageSelector } from './useLanguageSelector';
import { stateStore } from '../../global/state.store';
import { LanguagesList } from './languages-list';
import { LanguageSearch } from './language-search';
import { ExpandPanel } from '../expand-panel/expand-panel';
import { useNavigationReader } from '../../services/use-navigation-reader';
import { useTranslation } from '../../services/use-translation';

export const stringIncludeString = (str1: string, str2: string) =>
  str1.toLowerCase().includes(str2.toLowerCase());

const getLanguageData = (languageKey: string, languages: Language[]) =>
  languages.filter(({ value }) => value === languageKey)[0];

const rakbankFilter = (partner: string) => (language: Language) => {

  if (partner === 'rakbank') {
    const avaliableLanguages = ['en-US', 'ar'];

    return avaliableLanguages.includes(language.value);
  }

  return language;
}

export const LanguageSelector: FunctionalComponent = () => {
  const {
    setLanguage,
    language,
    widgetPageLang,
    paidProductsEnabled,
    liveTranslationsOptions,
    menuUserLanguageValue,
    updateMenuUserLanguageValue,
    partner,
  } = useContext(ApplicationContext);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const focusedItem = useRef<number>(null);
  const langSelectButton = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const languagesRef = useRef<HTMLButtonElement[]>([]);
  const originalPageLang = useRef<Language>(null);
  const selectedLanguageFocus = useRef<boolean>(false);
  const selectedLanguageCandidate = useRef<Language>(null);
  const [filter, setFilter] = useState('');
  const [isListFocused, setIsListFocused] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<Language>(null);
  const [isLoggedUser] = useConnect(sessionStore.isLogged$);
  const { translate } = useTranslation();
  const {
    focusNextLanguage,
    focusPreviousLanguage,
    focusFirstLanguageElement,
  } = useLanguageSelector(languagesRef, focusedItem);

  const isLiveTranslationAvailable = useMemo(
    () =>
      paidProductsEnabled.LIVE_TRANSLATIONS &&
      liveTranslationsOptions.is_enabled &&
      liveTranslationsOptions.translationAllowed,
    [liveTranslationsOptions, paidProductsEnabled.LIVE_TRANSLATIONS],
  );

  const closeLanguageSelector = () => {
    setIsListFocused(false);
    setShowLanguageMenu(false);
    selectedLanguageFocus.current = true;
  };
  const { sendNavigationReaderRequest } = useNavigationReader();
  const closeLanguageSelectorWithEsc = (event: KeyboardEvent) => {
    if (event.code === 'Escape') {
      stopEvents(event);
      closeLanguageSelector();
    }
  };

  const readContent = (e: InputEvent) => {
    const label = e.data;

    if (label) {
      sendNavigationReaderRequest(label);
    }
    if (e.inputType === 'deleteContentBackward') {
      sendNavigationReaderRequest(translate('widget.language.backspace'));
    }
    if (e.inputType === 'deleteContentForward') {
      sendNavigationReaderRequest(translate('widget.language.delete'));
    }

  };
  useEffect(() => {
    if (!widgetPageLang || !LANGUAGES) {
      return;
    }

    originalPageLang.current = getLanguageData(widgetPageLang, LANGUAGES);
  }, [widgetPageLang]);

  useEffect(() => {
    const findLanguage = getLanguageData(language, LANGUAGES);
    setSelectedLanguage(findLanguage);
  }, [language]);

  useEffect(() => {
    if (showLanguageMenu) {
      window.addEventListener('keydown', closeLanguageSelectorWithEsc, true);
    } else {
      window.removeEventListener('keydown', closeLanguageSelectorWithEsc, true);
    }
    if (isListFocused) {
      window.addEventListener('keydown', navigateMenu, true);
    } else {
      window.removeEventListener('keydown', navigateMenu, true);
    }

    return () => {
      window.removeEventListener('keydown', closeLanguageSelectorWithEsc, true);
      window.removeEventListener('keydown', navigateMenu, true);
    };
  }, [isListFocused, showLanguageMenu]);

  useEffect(() => {
    if (
      selectedLanguageFocus.current &&
      language === selectedLanguage?.value
    ) {
      langSelectButton.current.focus();
      selectedLanguageFocus.current = false;
    }
  }, [language, selectedLanguage])

  const toggleMenu = (event?) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setIsListFocused(false);
    setShowLanguageMenu((value) => !value);
  };

  const revertTranslation = () => {
    selectedLanguageCandidate.current = { ...originalPageLang.current };
    changeLanguage({});
    closeLanguageSelector();

    sendPostMessage({
      action: 'disableLiveTranslation',
    });
  };

  const selectLanguage = (newLanguage: Language) => {
    if (!newLanguage) return;

    if (newLanguage.value === originalPageLang.current?.value) {
      return revertTranslation();
    }

    selectedLanguageCandidate.current = newLanguage;

    if ((isLiveTranslationAvailable || isLoggedUser) && !newLanguage.noLiveTranslation) {
      applyToSite();
    } else {
      changeLanguage({});
    }
    closeLanguageSelector();
  };

  const applyToSite = () => {
    if (!isLiveTranslationAvailable && isLoggedUser) {
      return stateStore.state.next('main.logged.live-site-translations.main');
    }

    changeLanguage({ shouldNotTriggerLiveTranslation: true });

    sendPostMessage({
      action: 'translateWebsite',
      value: selectedLanguageCandidate.current.extraCode || selectedLanguageCandidate.current.value,
    });
  };

  const changeLanguage = ({
                            shouldNotTriggerLiveTranslation = false,
                          }: {
    shouldNotTriggerLiveTranslation?: boolean;
  }) => {
    const newSelectedLanguage = { ...selectedLanguageCandidate.current };

    setSelectedLanguage(newSelectedLanguage);

    sendPostMessage({
      action: 'setWidgetLanguage',
      langCode: newSelectedLanguage.value,
      shouldNotTriggerLiveTranslation,
    });

    loadDeferredJs(
      // eslint-disable-next-line
      `${'__CDN__' + 'frontend/locales/'}${newSelectedLanguage.value}.js`,
    )
      .then(() => {
        setLanguage(newSelectedLanguage.value);
        updateMenuUserLanguageValue({ value: newSelectedLanguage.value });
      })
      .catch((err) => {
        console.error(err);
        const findLanguage = getLanguageData(language, LANGUAGES);
        setSelectedLanguage(findLanguage);
      });
  };

  const stopEvents = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const navigateMenu = (e) => {
    // if (e.code === 'Escape') {
    //   stopEvents(e);
    //   closeLanguageSelector();
    // }

    if (e.code === 'ArrowLeft' || e.code === 'ArrowUp' || (e.code === 'Tab' && e.shiftKey)) {
      stopEvents(e);
      const previousLanguage = focusPreviousLanguage();

      if (!previousLanguage) {
        setIsListFocused(false);
        inputRef.current?.focus();
      }
    }

    if (e.code === 'ArrowRight' || e.code === 'ArrowDown' || (e.code === 'Tab' && !e.shiftKey)) {
      stopEvents(e);
      const nextLanguage = focusNextLanguage();

      if (!nextLanguage) {
        focusFirstLanguageElement();
      }
    }
  };
  const languages = useMemo(
      () =>
          LANGUAGES
              .filter(rakbankFilter(partner))
              .filter(
                  ({ name, value }) =>
                      stringIncludeString(name, filter) || stringIncludeString(value, filter),
              ),
      [filter, partner],
  );

  if (!menuUserLanguageValue?.liveLanguageEnabled && !isLiveTranslationAvailable) {
    return null;
  }

  const expandPanelReaderText = useMemo(
    () => `${translate('widget.language.label')} ${selectedLanguage?.name}`,
    [translate, selectedLanguage],
  );

  return (
    <div className="uwaw-lang-component">
      <ExpandPanel
        title={selectedLanguage?.name}
        readerLang={selectedLanguage?.value}
        readerText={expandPanelReaderText}
        icon={
          <CircleFlag
            className="uwaw-circle-flag"
            countryCode={selectedLanguage && selectedLanguage.flag}
          />
        }
        isOpen={showLanguageMenu}
        onToggle={toggleMenu}
        innerRef={langSelectButton}
      >
        <div className="uwaw-lang-list-wrap" data-uw-ignore-translate="true">
          <CustomScroll heightRelativeToParent="100%" allowOuterScroll={true}>
            {partner !== 'rakbank' &&
            <LanguageSearch
              reference={(el) => {
                inputRef.current = el;
              }}
              value={filter}
              onInput={(event) => {
                if (event.target instanceof HTMLInputElement) {
                  setFilter(event.target.value);
                }

                if (event instanceof InputEvent) {
                  readContent(event);
                }
              }}
              onFocus={(event) => {
                if (event.target instanceof HTMLInputElement && event.target.value) {
                  sendNavigationReaderRequest(event.target.value);
                }
              }}
            />}

            <LanguagesList
              languages={languages}
              active={selectedLanguage}
              languagesRef={languagesRef}
              selectLanguage={selectLanguage}
              focusedItem={focusedItem}
              setIsListFocused={setIsListFocused}
            />
          </CustomScroll>
        </div>
      </ExpandPanel>
    </div>
  );
};
